import { Options, Vue } from 'vue-class-component';
import {
  ComponentType,
  DeviceDTO,
  ILinkableObject,
  Slot,
  SlotStatus,
  ThermoActuatorType,
} from '@/../client-generator/generated-clients/plants';
import { PropType } from 'vue';
import { SlotRuleServiceType } from '@/core/services/slots-assign-rules-service/slots-assign-rules-interfaces';
import {
  convertThermoRuleServiceTypeToThermoActuatorType,
  getCurrentThermoActuatorType,
} from '@/core/utils/utils';

@Options({
  name: 'SlotItem',
  props: {
    device: {
      type: Object as PropType<DeviceDTO>,
      required: true,
    },
    objectSlot: {
      type: Object as PropType<Slot>,
      required: true,
    },
    isAdd: {
      type: Boolean,
      default: false,
    },
    linkableObject: {
      type: Object as PropType<ILinkableObject>,
    },
    ruleServiceType: {
      type: Number,
      default: -1,
    },
  },
})
export default class SlotItem extends Vue {
  objectSlot!: Slot;
  device!: DeviceDTO;
  isAdd!: boolean;
  linkableObject!: ILinkableObject;
  ruleServiceType!: SlotRuleServiceType; //this describes which rule service use

  readonly FirstElement = 0;
  readonly OneElement = 1;
  readonly TwoElements = 2;

  get isWizardLeftPanel() {
    return !(!this.linkableObject || this.isAdd);
  }

  get slotDotClass() {
    return {
      'device-slot-dot--not-configured':
        this.objectSlot.status === SlotStatus.NotConfigured,
      'device-slot-dot--not-used':
        this.objectSlot.status === SlotStatus.NotUsed,
      'device-slot-dot--configured':
        this.objectSlot.status === SlotStatus.Configured ||
        (this.objectSlot.status === SlotStatus.MarkedForLinking &&
          !this.isCommutable),
      'device-slot-dot--configured-mine':
        this.isConfiguredMine && this.isCommutable,
      'device-slot-dot--marked-for-linking':
        this.objectSlot.status === SlotStatus.MarkedForLinking &&
        this.isCommutable,
    };
  }

  get slotIcon() {
    if (
      this.device.componentTypes?.some(
        (c) => c === ComponentType.ShutterActuatorCommand
      ) &&
      !this.isWizardLeftPanel //this shows circles and squares in step2/3 when selecting slots
    ) {
      return 'has--triangles';
    }
    if (
      (this.isWizardLeftPanel &&
        this.ruleServiceType == SlotRuleServiceType.AUTOMATION_ACTUATOR) ||
      (!this.isWizardLeftPanel &&
        this.objectSlot.assignedComponentType === ComponentType.Actuator)
    ) {
      return 'has--circles';
    }
    if (
      (this.isWizardLeftPanel &&
        [
          SlotRuleServiceType.AUTOMATION_COMMAND,
          SlotRuleServiceType.COMMANDS_AND_GROUPS,
        ].includes(this.ruleServiceType)) ||
      (!this.isWizardLeftPanel &&
        this.objectSlot.assignedComponentType === ComponentType.Command)
    ) {
      return 'has--squares';
    }
    return '';
  }

  get isShutterActuatorDevice() {
    return this.device.componentTypes?.includes(
      ComponentType.ShutterActuatorCommand
    );
  }

  get isShutterActuatorCommandSlotSelectedInThisStep() {
    return (
      this.isShutterActuatorDevice &&
      this.objectSlot.assignedComponentType ===
        this.currentStepAssignableComponentType &&
      (this.objectSlot.status === SlotStatus.MarkedForLinking ||
        this.objectSlot.status === SlotStatus.Configured)
    );
  }

  get isConfiguredMine() {
    return (
      !!this.linkableObject &&
      this.objectSlot.status === SlotStatus.Configured &&
      this.objectSlot.linkedObjects?.some(
        (x) => x.id === this.linkableObject?.id
      )
    );
  }

  get isCommutable() {
    return (
      this.objectSlot.status === SlotStatus.NotConfigured ||
      ((this.isConfiguredMine ||
        this.objectSlot.status === SlotStatus.MarkedForLinking) &&
        (!this.isShutterActuatorDevice ||
          this.isShutterActuatorCommandSlotSelectedInThisStep) &&
        this.objectSlot.assignedComponentType ===
          this.currentStepAssignableComponentType &&
        this.currentThermoActuatorType ===
          this.currentStepAssignableThermoActuatorType)
    );
  }

  get isSlotSelected() {
    return (
      this.isConfiguredMine ||
      this.objectSlot.status === SlotStatus.MarkedForLinking
    );
  }

  get isLinkedWithCurrentRoomObject() {
    return (
      this.objectSlot.linkedObjects?.some(
        (linkedRoomObject) => linkedRoomObject.id == this.linkableObject.id
      ) ?? false
    );
  }

  get currentThermoActuatorType(): ThermoActuatorType {
    return getCurrentThermoActuatorType(
      this.objectSlot.thermoActuatorTypes,
      this.linkableObject.id
    );
  }

  get currentStepAssignableComponentType() {
    return {
      [SlotRuleServiceType.AUTOMATION_ACTUATOR]: ComponentType.Actuator,
      [SlotRuleServiceType.AUTOMATION_COMMAND]: ComponentType.Command,
      [SlotRuleServiceType.THERMO_COOLING_ACTUATORS]: ComponentType.Actuator,
      [SlotRuleServiceType.THERMO_HEATING_ACTUATORS]: ComponentType.Actuator,
      [SlotRuleServiceType.THERMO_SHARED_ACTUATORS]: ComponentType.Actuator,
      [SlotRuleServiceType.THERMO_PROBE]: ComponentType.Probe,
      [SlotRuleServiceType.THERMO_THERMO]: ComponentType.Thermo,
      [SlotRuleServiceType.THERMO_HEATING_PUMPS]: ComponentType.Actuator,
      [SlotRuleServiceType.THERMO_COOLING_PUMPS]: ComponentType.Actuator,
      [SlotRuleServiceType.THERMO_SHARED_PUMPS]: ComponentType.Actuator,
      [SlotRuleServiceType.COMMANDS_AND_GROUPS]: ComponentType.Command,
    }[this.ruleServiceType] as ComponentType;
  }

  get currentStepAssignableThermoActuatorType() {
    return convertThermoRuleServiceTypeToThermoActuatorType(
      this.ruleServiceType
    );
  }
}
